import { CalendarFilterCategoryIds } from './categories'
import { type CalendarFilterCategory, type CalendarFilterOption } from './types'

const byName = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
}).compare

/**
 * The options in name order, with each heading's rows kept under it and in name
 * order too. The lists arrive in whatever order the workspace keeps them, which
 * on a large workspace is no order a person can scan.
 */
export const alphabetically = (
  options: readonly CalendarFilterOption[]
): CalendarFilterOption[] => {
  const headingOf = (option: CalendarFilterOption) =>
    option.group ?? { id: option.id, label: option.label }

  return [...options].sort((a, b) => {
    const [headingA, headingB] = [headingOf(a), headingOf(b)]

    return (
      byName(headingA.label, headingB.label) ||
      headingA.id.localeCompare(headingB.id) ||
      Number(a.id !== headingA.id) - Number(b.id !== headingB.id) ||
      byName(a.label, b.label)
    )
  })
}

/**
 * The categories that list the workspace's own boards, channels, labels and
 * stories. The status list keeps the order the workflow runs in.
 */
const NAME_ORDERED: ReadonlySet<string> = new Set([
  CalendarFilterCategoryIds.board,
  CalendarFilterCategoryIds.channels,
  CalendarFilterCategoryIds.label,
  CalendarFilterCategoryIds.story,
])

/**
 * The categories as the filter drawer should list them.
 *
 * @note Sort only the copy the drawer draws. Other readers rank options by the
 * order the workspace keeps them in: colouring by a label set picks the label
 * that comes first in the set.
 */
export const listedByName = (
  categories: readonly CalendarFilterCategory[]
): CalendarFilterCategory[] =>
  categories.map((category) =>
    NAME_ORDERED.has(category.id)
      ? { ...category, options: alphabetically(category.options) }
      : category
  )
